import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import API_BASE_URL from '../config';
import { trackEvent } from '../utils/analytics';

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) { setError('Please fill in all fields.'); return; }
    setError('');
    setLoading(true);
    trackEvent("contact_form_submitted");

    try {
      await axios.post(`${API_BASE_URL}/api/contact`, formData);
      setSubmitted(true);
      trackEvent("contact_form_success");
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
      trackEvent("contact_form_failed", { reason: err.response?.data?.error || "request_failed" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={cardStyle}>
        <h1 style={titleStyle}>Contact Us</h1>
        <div style={glowLineStyle} />
        <p style={textStyle}>Have questions, feedback, or need support? We'd love to hear from you.</p>

        {submitted ? (
          <div style={responseStyle}>
            <p style={responseTextStyle}>Thank you for reaching out! We'll get back to you within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inputStyle} placeholder="Your name" />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={inputStyle} placeholder="your@email.com" />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Message</label>
              <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }} placeholder="How can we help?" />
            </div>
            {error && <p style={errorStyle}>{error}</p>}
            <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={buttonStyle} disabled={loading}>
              {loading ? 'Sending...' : 'Send Message'}
            </motion.button>
          </form>
        )}

        <div style={contactGridStyle}>
          <a href="mailto:support@zenqb.com" style={{ ...contactCardStyle, textDecoration: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <span style={{ fontSize: '1.5rem' }}>📧</span>
            <h3 style={contactTitleStyle}>Email</h3>
            <p style={contactTextStyle}>support@zenqb.com</p>
          </a>
          <a href="https://wa.me/919072345678" target="_blank" rel="noopener noreferrer" style={{ ...contactCardStyle, textDecoration: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <span style={{ fontSize: '1.5rem' }}>💬</span>
            <h3 style={contactTitleStyle}>WhatsApp</h3>
            <p style={contactTextStyle}>Chat with us on WhatsApp</p>
          </a>
        </div>
      </motion.div>
    </div>
  );
}

const containerStyle = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', background: '#f8f9fc', fontFamily: "'Inter', 'Segoe UI', sans-serif" };
const cardStyle = { background: '#ffffff', borderRadius: '24px', padding: '60px 40px', maxWidth: '900px', width: '100%', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' };
const titleStyle = { fontSize: '3rem', fontWeight: '700', marginBottom: '8px', color: '#111827', textAlign: 'center', letterSpacing: '-0.02em', fontFamily: "'Space Grotesk', 'Sora', sans-serif" };
const glowLineStyle = { width: '80px', height: '4px', background: 'linear-gradient(90deg, #6366f1, #a855f7)', borderRadius: '2px', margin: '0 auto 40px' };
const textStyle = { fontSize: '1.15rem', lineHeight: '1.8', marginBottom: '28px', color: '#4b5563', textAlign: 'center', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' };
const inputGroupStyle = { marginBottom: '20px' };
const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle = { width: '100%', padding: '12px 16px', fontSize: '0.95rem', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#ffffff', color: '#111827', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const errorStyle = { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '10px', fontSize: '0.85rem', marginBottom: '16px' };
const buttonStyle = { width: '100%', padding: '14px', border: 'none', borderRadius: '12px', background: 'linear-gradient(135deg, #6366f1, #a855f7)', color: 'white', fontSize: '1rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)' };
const responseStyle = { background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.15)', borderRadius: '12px', padding: '24px', textAlign: 'center' };
const responseTextStyle = { color: '#6366f1', fontSize: '1rem', margin: 0 };
const contactGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '40px' };
const contactCardStyle = { background: '#ffffff', borderRadius: '16px', padding: '24px', textAlign: 'center', border: '1px solid #e5e7eb' };
const contactTitleStyle = { fontSize: '1rem', fontWeight: '600', color: '#111827', margin: '8px 0 4px' };
const contactTextStyle = { fontSize: '0.85rem', color: '#6b7280', margin: 0 };

export default Contact;
